/**
 * ERP-FIN-03 GL-JOURNAL-01: General Ledger Journal Workspace
 * Route: /gl/journals
 * Cap: ledger.read (view), ledger.manage (create)
 *
 * Design (GATE5-FIN-04):
 *  - Manual journals only (GL-APPROVAL-04)
 *  - COA v1 codes accepted (GL-APPROVAL-01)
 *  - Maker-checker SoD enforced (GL-APPROVAL-05)
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { glApi, orgApi, formatError } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { toast } from "sonner";
import {
  BookOpen, Plus, RefreshCw, Loader2, ChevronRight,
  Filter, Search, AlertCircle, CheckCircle, Clock,
  FileText, Send, ShieldCheck, XCircle, RotateCcw,
  TrendingUp, ArrowRight,
} from "lucide-react";

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CFG = {
  DRAFT:            { label: "Draft",           color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]",   icon: FileText },
  PENDING_APPROVAL: { label: "Pending Approval", color: "text-[#8C4B00]", bg: "bg-[#FFF4E5]",   icon: Clock },
  APPROVED:         { label: "Approved",         color: "text-[#0176D3]", bg: "bg-[#E8F4FD]",   icon: CheckCircle },
  POSTED:           { label: "Posted",           color: "text-[#2E844A]", bg: "bg-[#EAF5EE]",   icon: ShieldCheck },
  REVERSED:         { label: "Reversed",         color: "text-[#706E6B]", bg: "bg-[#F3F3F3]",   icon: RotateCcw },
  VOID:             { label: "Void",             color: "text-[#BA0517]", bg: "bg-[#FCEBEC]",   icon: XCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.DRAFT;
  const Icon = cfg.icon;
  return (
    <span data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color}`}>
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}

function fmt(minor) {
  if (minor == null) return "—";
  return "INR " + (minor / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 });
}

// ── Create Journal Modal ───────────────────────────────────────────────────────

function CreateJournalModal({ legalEntities, onClose, onCreated }) {
  const [form, setForm] = useState({
    legal_entity_id: legalEntities[0]?._id || "",
    accounting_date: new Date().toISOString().slice(0, 10),
    narration: "",
    currency_code: "INR",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.narration.trim() || form.narration.length < 5) {
      toast.error("Narration must be at least 5 characters");
      return;
    }
    setSaving(true);
    try {
      const res = await glApi.createJournal(form);
      toast.success(`Journal ${res.data.journal_ref || res.data.journal?.journal_ref} created`);
      onCreated(res.data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-journal-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <h2 className="text-base font-bold text-[#1C1917]">New GL Journal</h2>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#1C1917]">
            <XCircle size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#706E6B] mb-1">Legal Entity *</label>
            <select
              data-testid="create-journal-le"
              className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]"
              value={form.legal_entity_id}
              onChange={e => setForm(f => ({ ...f, legal_entity_id: e.target.value }))}
              required
            >
              <option value="">— Select legal entity —</option>
              {legalEntities.map(le => (
                <option key={le._id || le.id} value={le._id || le.id}>{le.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#706E6B] mb-1">Accounting Date *</label>
            <input
              data-testid="create-journal-date"
              type="date"
              className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]"
              value={form.accounting_date}
              onChange={e => setForm(f => ({ ...f, accounting_date: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#706E6B] mb-1">Narration * (min 5 chars)</label>
            <textarea
              data-testid="create-journal-narration"
              className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3] resize-none"
              rows={3}
              placeholder="e.g. Deposit receipt from Priya Sharma Wedding — Oct 2026"
              value={form.narration}
              onChange={e => setForm(f => ({ ...f, narration: e.target.value }))}
              required
              minLength={5}
            />
          </div>
          <div className="bg-[#F8F6F3] rounded-lg p-3 text-xs text-[#706E6B]">
            <strong>Note:</strong> Journal is created as DRAFT. Add DR/CR lines on the detail page, then submit for approval (maker-checker required).
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 border border-[#E5E5E5] rounded-lg px-4 py-2 text-sm font-semibold text-[#706E6B] hover:bg-[#F3F3F3]">
              Cancel
            </button>
            <button type="submit" disabled={saving} data-testid="create-journal-submit"
              className="flex-1 bg-[#1C1917] text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-[#3B3735] disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Create Journal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Status filter tab ─────────────────────────────────────────────────────────

function TabFilter({ active, label, count, value, onClick }) {
  return (
    <button
      data-testid={`tab-filter-${value || "all"}`}
      onClick={() => onClick(value)}
      className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
        active
          ? "bg-[#1C1917] text-white"
          : "text-[#706E6B] hover:bg-[#F3F3F3]"
      }`}
    >
      {label} {count != null ? <span className="ml-1 opacity-75">({count})</span> : ""}
    </button>
  );
}

// ── Main workspace ────────────────────────────────────────────────────────────

export default function GLJournalWorkspace() {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const canManage = hasCapability(user, "ledger.manage");
  const canRead   = hasCapability(user, "ledger.read");

  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [statusFilter,  setStatusFilter]  = useState("");
  const [search,        setSearch]        = useState("");
  const [page,          setPage]          = useState(1);
  const [showCreate,    setShowCreate]    = useState(false);
  const [legalEntities, setLegalEntities] = useState([]);
  const [trialBalance,  setTrialBalance]  = useState(null);
  const [tbLoading,     setTbLoading]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 25 };
      if (statusFilter) params.status = statusFilter;
      const res = await glApi.listJournals(params);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load GL journals: " + formatError(err));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Load legal entities for the create modal
    orgApi.listLegalEntities().then(r => setLegalEntities(r.data?.legal_entities || r.data || [])).catch(() => {});
  }, []);

  const loadTrialBalance = async () => {
    setTbLoading(true);
    try {
      const res = await glApi.getTrialBalance();
      setTrialBalance(res.data);
    } catch (err) {
      toast.error("Failed to load trial balance: " + formatError(err));
    } finally {
      setTbLoading(false);
    }
  };

  const counts = data?.counts || {};
  const journals = data?.journals || [];

  const filtered = search
    ? journals.filter(j =>
        (j.journal_ref || "").toLowerCase().includes(search.toLowerCase()) ||
        (j.narration  || "").toLowerCase().includes(search.toLowerCase())
      )
    : journals;

  if (!canRead) {
    return (
      <div className="p-8 text-center text-[#706E6B]" data-testid="gl-no-access">
        <AlertCircle size={32} className="mx-auto mb-3 text-[#E9A820]" />
        <p className="font-semibold">Access denied</p>
        <p className="text-sm mt-1">You need <code>ledger.read</code> capability to view GL journals.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6F3]" data-testid="gl-journal-workspace">

      {/* Header */}
      <div className="bg-white border-b border-[#E5E5E5] px-8 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#1C1917] rounded-lg">
              <BookOpen size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#1C1917]">GL Journals</h1>
              <p className="text-xs text-[#706E6B] mt-0.5">
                General Ledger · Manual journals · Maker-checker enforced
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={loadTrialBalance} disabled={tbLoading}
              data-testid="btn-trial-balance"
              className="flex items-center gap-2 border border-[#E5E5E5] rounded-lg px-4 py-2 text-sm font-semibold text-[#706E6B] hover:bg-[#F3F3F3] disabled:opacity-60">
              {tbLoading ? <Loader2 size={14} className="animate-spin" /> : <TrendingUp size={14} />}
              Trial Balance
            </button>
            <button onClick={load} disabled={loading}
              data-testid="btn-refresh-journals"
              className="flex items-center gap-2 border border-[#E5E5E5] rounded-lg px-4 py-2 text-sm font-semibold text-[#706E6B] hover:bg-[#F3F3F3] disabled:opacity-60">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            {canManage && (
              <button onClick={() => setShowCreate(true)}
                data-testid="btn-new-journal"
                className="flex items-center gap-2 bg-[#1C1917] text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-[#3B3735]">
                <Plus size={14} />
                New Journal
              </button>
            )}
          </div>
        </div>

        {/* Status tabs */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <TabFilter active={!statusFilter} label="All" count={data?.total} value="" onClick={setStatusFilter} />
          <TabFilter active={statusFilter === "DRAFT"}            label="Draft"            count={counts.DRAFT}            value="DRAFT"            onClick={setStatusFilter} />
          <TabFilter active={statusFilter === "PENDING_APPROVAL"} label="Pending Approval" count={counts.PENDING_APPROVAL} value="PENDING_APPROVAL" onClick={setStatusFilter} />
          <TabFilter active={statusFilter === "APPROVED"}         label="Approved"         count={counts.APPROVED}         value="APPROVED"         onClick={setStatusFilter} />
          <TabFilter active={statusFilter === "POSTED"}           label="Posted"           count={counts.POSTED}           value="POSTED"           onClick={setStatusFilter} />
          <TabFilter active={statusFilter === "VOID"}             label="Void"             count={counts.VOID}             value="VOID"             onClick={setStatusFilter} />
        </div>
      </div>

      <div className="px-8 py-6 space-y-6">

        {/* Trial Balance Panel */}
        {trialBalance && (
          <div className="bg-white border border-[#E5E5E5] rounded-xl p-6" data-testid="trial-balance-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-[#1C1917]">Trial Balance — All Posted Journals</h2>
              <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${
                trialBalance.is_balanced ? "bg-[#EAF5EE] text-[#2E844A]" : "bg-[#FCEBEC] text-[#BA0517]"
              }`}>
                {trialBalance.is_balanced ? <CheckCircle size={11} /> : <AlertCircle size={11} />}
                {trialBalance.is_balanced ? "Balanced" : "Unbalanced"}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4 text-center">
              <div>
                <p className="text-xs text-[#706E6B]">Total Debit</p>
                <p className="text-base font-bold text-[#1C1917]">{fmt(trialBalance.total_debit)}</p>
              </div>
              <div>
                <p className="text-xs text-[#706E6B]">Total Credit</p>
                <p className="text-base font-bold text-[#1C1917]">{fmt(trialBalance.total_credit)}</p>
              </div>
              <div>
                <p className="text-xs text-[#706E6B]">Journals Posted</p>
                <p className="text-base font-bold text-[#1C1917]">{trialBalance.journal_count}</p>
              </div>
            </div>
            {trialBalance.accounts?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#E5E5E5] text-[#706E6B]">
                      <th className="text-left py-2 font-semibold">Account Code</th>
                      <th className="text-left py-2 font-semibold">Name</th>
                      <th className="text-right py-2 font-semibold">Debit</th>
                      <th className="text-right py-2 font-semibold">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trialBalance.accounts.map(a => (
                      <tr key={a.account_code} className="border-b border-[#F3F3F3]">
                        <td className="py-1.5 font-mono font-semibold text-[#1C1917]">{a.account_code}</td>
                        <td className="py-1.5 text-[#3B3735]">{a.account_name || "—"}</td>
                        <td className="py-1.5 text-right text-[#2E844A]">{a.debit_minor ? fmt(a.debit_minor) : "—"}</td>
                        <td className="py-1.5 text-right text-[#0176D3]">{a.credit_minor ? fmt(a.credit_minor) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {trialBalance.accounts?.length === 0 && (
              <p className="text-sm text-[#706E6B] text-center py-4">No posted journals yet.</p>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative w-80">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" />
          <input
            data-testid="gl-search"
            className="w-full border border-[#E5E5E5] rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]"
            placeholder="Search by reference or narration…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Journal list */}
        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="gl-journals-loading">
            <Loader2 size={24} className="animate-spin text-[#0176D3]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-xl p-10 text-center" data-testid="gl-journals-empty">
            <BookOpen size={32} className="mx-auto mb-3 text-[#E5E5E5]" />
            <p className="text-sm font-semibold text-[#1C1917]">No GL journals found</p>
            <p className="text-xs text-[#706E6B] mt-1">
              {canManage ? 'Click "New Journal" to create your first manual journal entry.' : "No journals match the current filter."}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid="gl-journals-list">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E5E5] bg-[#F8F6F3]">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#706E6B]">Reference</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#706E6B]">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#706E6B]">Narration</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-[#706E6B]">Debit</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-[#706E6B]">Credit</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#706E6B]">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#706E6B]">Type</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(j => (
                  <tr key={j.id}
                    data-testid={`journal-row-${j.id}`}
                    className="border-b border-[#F3F3F3] hover:bg-[#FAFAF9] cursor-pointer"
                    onClick={() => navigate(`/gl/journals/${j.id}`)}
                  >
                    <td className="px-4 py-3 font-mono font-semibold text-[#1C1917] text-xs">
                      {j.journal_ref}
                    </td>
                    <td className="px-4 py-3 text-[#3B3735] text-xs whitespace-nowrap">
                      {j.accounting_date}
                    </td>
                    <td className="px-4 py-3 text-[#3B3735] max-w-[240px] truncate text-xs">
                      {j.narration}
                    </td>
                    <td className="px-4 py-3 text-right text-[#2E844A] font-semibold text-xs whitespace-nowrap">
                      {fmt(j.total_debit_minor)}
                    </td>
                    <td className="px-4 py-3 text-right text-[#0176D3] font-semibold text-xs whitespace-nowrap">
                      {fmt(j.total_credit_minor)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={j.status} />
                    </td>
                    <td className="px-4 py-3 text-xs text-[#706E6B]">
                      {j.journal_type}
                    </td>
                    <td className="px-4 py-3">
                      <ChevronRight size={14} className="text-[#706E6B]" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {data?.total > 25 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E5E5]">
                <p className="text-xs text-[#706E6B]">
                  Page {page} of {Math.ceil(data.total / 25)} · {data.total} total
                </p>
                <div className="flex gap-2">
                  <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                    className="px-3 py-1 text-xs border border-[#E5E5E5] rounded disabled:opacity-40 hover:bg-[#F3F3F3]">
                    Previous
                  </button>
                  <button disabled={page * 25 >= data.total} onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1 text-xs border border-[#E5E5E5] rounded disabled:opacity-40 hover:bg-[#F3F3F3]">
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <CreateJournalModal
          legalEntities={legalEntities}
          onClose={() => setShowCreate(false)}
          onCreated={(result) => {
            setShowCreate(false);
            // Navigate to detail for line addition
            const jid = result.journal_id || result.id;
            if (jid) navigate(`/gl/journals/${jid}`);
            else load();
          }}
        />
      )}
    </div>
  );
}
